// Copyright (c) Ping. All rights reserved.

import type { MiniProfileAnchorMeasurement, MiniProfileAnchorRect, MiniProfileSide } from './types';

/** Default mini-profile card size used to clamp coordinates to viewport. */
export const MINI_PROFILE_CARD_DEFAULT_WIDTH = 320;
export const MINI_PROFILE_CARD_DEFAULT_HEIGHT = 220;
export const MINI_PROFILE_VIEWPORT_PADDING = 8;

interface ClampArgs {
  readonly anchor: MiniProfileAnchorMeasurement;
  readonly side: MiniProfileSide;
  readonly offset: number;
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly viewport: { readonly width: number; readonly height: number };
}

/**
 * Pure geometry: places the card next to the anchor according to `side`,
 * then clamps so the card stays inside `viewport` minus
 * {@link MINI_PROFILE_VIEWPORT_PADDING}.
 *
 * Lives in its own module so `react-native`-free callers (the unit tests,
 * SSR contexts) can import it without dragging in the RN runtime.
 */
export function clampToViewport(args: ClampArgs): MiniProfileAnchorRect {
  const { anchor, offset, cardWidth, cardHeight, viewport } = args;
  const pad = MINI_PROFILE_VIEWPORT_PADDING;

  // FLIP: if the preferred side doesn't have room for the card, flip to the
  // opposite side when that side has more space. Keeps the popover/menu fully
  // on-screen and visually attached to its trigger instead of clamping into it.
  const need = (size: number): number => size + offset + pad;
  const spaceBelow = viewport.height - (anchor.y + anchor.height);
  const spaceAbove = anchor.y;
  const spaceRight = viewport.width - (anchor.x + anchor.width);
  const spaceLeft = anchor.x;

  let side = args.side;
  if (side === 'bottom' && spaceBelow < need(cardHeight) && spaceAbove > spaceBelow) {
    side = 'top';
  } else if (side === 'top' && spaceAbove < need(cardHeight) && spaceBelow > spaceAbove) {
    side = 'bottom';
  } else if (side === 'right' && spaceRight < need(cardWidth) && spaceLeft > spaceRight) {
    side = 'left';
  } else if (side === 'left' && spaceLeft < need(cardWidth) && spaceRight > spaceLeft) {
    side = 'right';
  }

  let top = 0;
  let left = 0;
  switch (side) {
    case 'top':
      top = anchor.y - cardHeight - offset;
      left = anchor.x + anchor.width / 2 - cardWidth / 2;
      break;
    case 'bottom':
      top = anchor.y + anchor.height + offset;
      left = anchor.x + anchor.width / 2 - cardWidth / 2;
      break;
    case 'left':
      top = anchor.y + anchor.height / 2 - cardHeight / 2;
      left = anchor.x - cardWidth - offset;
      break;
    case 'right':
      top = anchor.y + anchor.height / 2 - cardHeight / 2;
      left = anchor.x + anchor.width + offset;
      break;
  }

  const maxLeft = viewport.width - cardWidth - MINI_PROFILE_VIEWPORT_PADDING;
  const maxTop = viewport.height - cardHeight - MINI_PROFILE_VIEWPORT_PADDING;
  const clampedLeft = Math.max(MINI_PROFILE_VIEWPORT_PADDING, Math.min(maxLeft, left));
  const clampedTop = Math.max(MINI_PROFILE_VIEWPORT_PADDING, Math.min(maxTop, top));

  return {
    top: clampedTop,
    left: clampedLeft,
    visible: true,
  };
}

interface AnchorRefLike {
  readonly current: unknown;
}

/**
 * Reads a measurement from any RN/DOM ref. Returns `null` when no element is
 * currently mounted (so callers can hold the previous rect).
 */
export function readAnchorMeasurement(
  anchorRef: AnchorRefLike,
): MiniProfileAnchorMeasurement | null {
  const node = anchorRef.current as
    | { getBoundingClientRect?: () => DOMRect }
    | { measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void }
    | null;
  if (node === null || node === undefined) {
    return null;
  }
  const domNode = node as { getBoundingClientRect?: () => DOMRect };
  if (typeof domNode.getBoundingClientRect === 'function') {
    const r = domNode.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }
  const rnNode = node as {
    measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
  };
  if (typeof rnNode.measureInWindow === 'function') {
    let captured: MiniProfileAnchorMeasurement | null = null;
    rnNode.measureInWindow((x, y, width, height) => {
      captured = { x, y, width, height };
    });
    return captured;
  }
  return null;
}
